// Base URL prefixes for different backend systems
export const API_PATH_PREFIX = '/partnerApi/v1';
export const LARAVEL_API_PATH_PREFIX = '/api/v4/partner';

// HTTP methods that can be scoped per-endpoint during migration.
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// Endpoint configuration: maps endpoint paths to the Laravel API.
// This allows gradual migration from the old API to Laravel.
//
// Each entry is either:
//   - a bare string         → ALL methods for that path route to Laravel
//   - { path, methods: [] } → ONLY the listed methods route to Laravel; the
//     remaining methods stay on the old API, so the same path can be split
//     across both backends during migration (e.g. writes cut over before reads).
type LaravelEndpoint = string | { path: string; methods: HttpMethod[] };

const LARAVEL_ENDPOINTS: LaravelEndpoint[] = [
  '/branch-services',
  '/branch-services/:serviceId',
  '/branch-services/:branchServiceId/availability/:agentId',
  '/branch-services/:branchServiceId/availability',
  '/branch-services/:serviceId/thumbnail',
  '/branches/:branchId/thumbnail',
  '/extra-field-types',
  '/extra-field-types/:uuid',
  '/extra-field-types/:uuid/values/count',
  '/extra-field-types/:relatedTo/reorder',
  '/api-keys',
  '/api-keys/:apiKeyId',
  '/api-keys/:apiKeyId/regenerate',
  '/api-keys/scopes',
  '/product-groups',
  '/product-groups/:productGroupId',
  '/exports',
  '/exports/:exportId',
  '/exports/:exportId/download',
  '/notifications',
  '/notifications/unread-count',
  '/notifications/:notificationId/read',
  '/notifications/:notificationId',
  '/notifications/read-all',
  '/customer-files/:customerFileId',
  '/leads/:leadId',
  '/leads/:leadId/activities',
  '/calendar-events',
  '/calendar-events/:calendarEventId',
  '/calendar-events/recurring',
  '/service-requests/calendar-unavailability',
  '/lead-sources',
  '/lead-sources/:sourceId',
  '/lead-sources/:sourceId/default',
  '/offerings',
  '/offerings/:offeringId',
  '/email-templates',
  '/email-templates/:uuid/render-preview',
  '/leads/:leadId/mail-threads',
  '/leads/:leadId/mail-threads/messages',
  '/leads/:leadId/mail-messages/:messageId',
  '/leads/:leadId/mail-messages/:messageId/attachments/:attachmentId',
  '/leads/:leadId/mail/send',
  '/leads/:leadId/mail-messages/:messageId/reply',
  '/mail/mailboxes',
  '/mail/mailboxes/sync',
  '/mail/mailboxes/users',
  '/mail/mailboxes/users/:userUuid',
  '/mail/mailboxes/:address',
  '/mail/me/mailbox',
  '/mail/capabilities',
  '/email-templates/:uuid',
  '/email-images',
  '/files',
  '/files/:fileId',
  '/files/:fileId/usage',
  '/email-templates/:uuid/test',
  '/email-templates/:uuid/detach',
  '/email-templates/:uuid/draft',
  '/email-templates/:uuid/draft/publish',
  '/email-templates/:uuid/history',
  '/email-templates/:uuid/history/:historyId/restore',
  '/email-template-types',
  '/email-variables',
  '/email-preview-samples',
  '/email-preview-samples/:type/:id',
  '/email-component-labels',
  '/template-fragments',
  '/template-fragments/:fragmentId',
  '/email-settings',
  '/partner-languages',
  // Journey config *scope* only. The journey-config view and its PUT/PATCH
  // writes still go to the legacy API; these three endpoints exist only in
  // Laravel, and both backends write the same `configurations` table.
  '/journeys/configuration-scopes',
  {
    path: '/journeys/:journeyId/configurations/:configurationKey',
    methods: ['DELETE'],
  },
  '/journeys/:journeyId/configurations/:configurationKey/deviate',
  '/journeys/:journeyId/tracking',
  '/journeys/:journeyId/email-config',
  '/journeys/:journeyId/email-config/deactivate',
  '/journeys/:journeyId/email-templates',
  '/journeys/:journeyId/email-templates/:emailType',
  '/journeys/:journeyId/email-templates/:emailType/test',
  '/journeys/:journeyId/campaign-emails/:templateId',
  '/journeys/:journeyId/marketing-automations',
  '/journeys/:journeyId/marketing-automations/:automationId',
  '/journeys/:journeyId/mailer',
  '/partner-translations',
  '/partner-translations/:uuid',
  '/mailers',
  '/mailers/:uuid',
  '/mailers/:uuid/test',
  '/sales-influencers',
  '/sales-influencers/:salesInfluencerId',
  '/skills',
  '/skills/:skillId',
  // Microsoft Entra SSO (Laravel-owned end to end, incl. the integration
  // endpoints — the legacy app is not touched; see docs/plans/microsoft-entra-sso.md)
  '/auth/sso/microsoft',
  '/auth/sso/exchange',
  '/integrations/microsoft_sso/enable',
  '/integrations/microsoft_sso/disable',
  '/integrations/microsoft_sso/groups',
  '/integrations/microsoft_sso',
  '/integrations/carat',
  '/integrations/carat/plannings',
  '/integrations/carat/plannings/:planningId',
  '/integration-types',
  // Only the trickpark-specific integration endpoints live in Laravel; the
  // generic /integrations list + config edit and the other types'
  // enable/disable stay on the old API. Lifecycle goes through the generic
  // {type} routes on Laravel (status GET, enable, disable); the base path is
  // method-scoped so a PUT/PATCH config edit keeps routing to the old API.
  { path: '/integrations/trickpark', methods: ['GET'] },
  '/integrations/trickpark/enable',
  '/integrations/trickpark/disable',
  '/integrations/trickpark/user-mappings',
  '/integrations/trickpark/user-mappings/:mappingId',
  '/integrations/trickpark/sessions',
  '/integrations/trickpark/sessions/:sessionId',
  { path: '/leads', methods: ['POST', 'GET'] },
  { path: '/tasks/:taskId', methods: ['GET'] },
  { path: '/tasks', methods: ['GET'] },
  { path: '/users/:userId', methods: ['GET', 'PATCH'] },
  // Per-type removal of a user's integration row (manual mapping or calendar
  // connection). Laravel-only — the legacy API has no counterpart.
  '/users/:userId/integrations/:integrationType',
  // Only the list is migrated — user create (POST) and bulk delete (DELETE)
  // share this path and stay on the old API.
  { path: '/users', methods: ['GET'] },
  // Bulk skills on users (Laravel-only).
  { path: '/users/bulk', methods: ['POST'] },
  // Sales goal plans live in Laravel only (conversional-laravel docs/goals.md).
  '/goal-plans',
  '/goal-plans/lookup',
  '/goal-plans/mine',
  '/goal-plans/:planId',
  '/goal-plans/:planId/targets',
  '/goal-plans/:planId/recalculate',
  // B2B contacts are Laravel-only. `POST /customers` is too, but the customer
  // list (GET) stays on the old API.
  { path: '/customers', methods: ['POST'] },
  '/customers/:customerId/contacts',
  '/customers/:customerId/contacts/:contactId',
  '/customers/:customerId/primary-contact',
  '/leads/:leadId/contacts',
  '/leads/:leadId/primary-contact',
  // Contacts of every customer the user may see (paginated).
  '/contacts',
];

function normalizePath(input: string): string {
  // strip origin if a full URL was passed
  let path = input.replace(/^https?:\/\/[^/]+/i, '');

  // remove query + hash
  path = path.split('?')[0].split('#')[0];

  // ensure leading slash
  if (!path.startsWith('/')) path = `/${path}`;

  // if caller accidentally passes already-prefixed URLs, remove prefixes
  if (path.startsWith(LARAVEL_API_PATH_PREFIX))
    path = path.slice(LARAVEL_API_PATH_PREFIX.length) || '/';
  if (path.startsWith(API_PATH_PREFIX))
    path = path.slice(API_PATH_PREFIX.length) || '/';

  // remove trailing slash (except root)
  if (path.length > 1) path = path.replace(/\/+$/, '');

  return path;
}

function endpointToRegex(template: string): RegExp {
  // escape regex special chars except ":" params and "/"
  const escaped = template.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = escaped.replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${pattern}$`);
}

// Precompile each endpoint to { regex, methods }.
// methods === null → all methods match (bare-string entries).
const LARAVEL_MATCHERS = LARAVEL_ENDPOINTS.map((entry) => {
  const path = typeof entry === 'string' ? entry : entry.path;
  const methods =
    typeof entry === 'string'
      ? null
      : new Set(entry.methods.map((m) => m.toUpperCase()));
  return { regex: endpointToRegex(path), methods };
});

export function getApiPrefix(path: string, method?: string): string {
  const basePath = normalizePath(path);
  const upperMethod = method?.toUpperCase();

  const isLaravel = LARAVEL_MATCHERS.some(
    ({ regex, methods }) =>
      regex.test(basePath) &&
      // null = all methods; otherwise the request method must be whitelisted.
      // A method-scoped entry never matches when no method is supplied (safe default).
      (methods === null || (!!upperMethod && methods.has(upperMethod))),
  );

  return isLaravel ? LARAVEL_API_PATH_PREFIX : API_PATH_PREFIX;
}

// region CSV Exports (Laravel Partner API — see LARAVEL_ENDPOINTS above)
export const CREATE_EXPORT = '/exports';
export const GET_EXPORTS = '/exports';
export const GET_EXPORT = '/exports/:exportId';
export const DOWNLOAD_EXPORT = '/exports/:exportId/download';
// endregion

// region Email Center (Laravel Partner API — see LARAVEL_ENDPOINTS above)
export const FETCH_EMAIL_TEMPLATES = '/email-templates';
export const EMAIL_TEMPLATE = '/email-templates/:uuid';
// Unpublished edits and version history. A draft lives apart from the template's
// own fields, so the template keeps sending its published version until publish.
// Images used inside email bodies. Shared bucket, `email/{partner_id}/` folder.
export const EMAIL_IMAGES = '/email-images';

// The partner file library: everything uploaded by the partner — images, fonts
// and documents — wherever it was uploaded from. `url` on a file is where it can
// actually be fetched from and differs per environment, so it is always read
// from the response and never assembled here.
export const FILES = '/files';
export const FILE = '/files/:fileId';
export const FILE_USAGE = '/files/:fileId/usage';
export const EMAIL_TEMPLATE_TEST = '/email-templates/:uuid/test';
export const EMAIL_TEMPLATE_DETACH = '/email-templates/:uuid/detach';
export const EMAIL_TEMPLATE_DRAFT = '/email-templates/:uuid/draft';
export const EMAIL_TEMPLATE_DRAFT_PUBLISH =
  '/email-templates/:uuid/draft/publish';
export const EMAIL_TEMPLATE_HISTORY = '/email-templates/:uuid/history';
export const EMAIL_TEMPLATE_HISTORY_RESTORE =
  '/email-templates/:uuid/history/:historyId/restore';
export const FETCH_EMAIL_TEMPLATE_TYPES = '/email-template-types';
export const FETCH_EMAIL_VARIABLES = '/email-variables';
// Real leads/appointments an email preview can be populated from: the list to
// pick from, then that record's send-time variables.
export const EMAIL_PREVIEW_SAMPLES = '/email-preview-samples';
export const EMAIL_PREVIEW_SAMPLE = '/email-preview-samples/:type/:id';
export const EMAIL_COMPONENT_LABELS = '/email-component-labels';
export const TEMPLATE_FRAGMENTS = '/template-fragments';
export const TEMPLATE_FRAGMENT = '/template-fragments/:fragmentId';
// What still references a fragment — the pre-flight before offering its delete.
export const TEMPLATE_FRAGMENT_USAGE = '/template-fragments/:fragmentId/usage';
export const EMAIL_SETTINGS = '/email-settings';
export const PARTNER_LANGUAGES = '/partner-languages';
// ⚠️ {journeyId} means different things per resource (backend contract):
//   email-config            → journey HASHID
//   email-templates / marketing-automations → RAW NUMERIC lead_source id (a hashid 500s)
export const JOURNEY_EMAIL_CONFIG = '/journeys/:journeyId/email-config';
/** Store an empty `email_config` row — the lead source sends no emails. */
export const JOURNEY_EMAIL_CONFIG_DEACTIVATE =
  '/journeys/:journeyId/email-config/deactivate';
export const JOURNEY_EMAIL_TEMPLATES = '/journeys/:journeyId/email-templates';
export const JOURNEY_EMAIL_TEMPLATE =
  '/journeys/:journeyId/email-templates/:emailType';
export const JOURNEY_EMAIL_TEMPLATE_TEST =
  '/journeys/:journeyId/email-templates/:emailType/test';
/**
 * One lead source's Newsletter2Go campaign mail, addressed by template uuid.
 *
 * NL2Go marketing mail is configured as campaign triggers on the journey, so it
 * is reached by neither the journey links above nor the automations — PUT
 * switches it off for this source, DELETE takes it off the source entirely.
 */
export const JOURNEY_CAMPAIGN_EMAIL =
  '/journeys/:journeyId/campaign-emails/:templateId';
export const JOURNEY_MARKETING_AUTOMATIONS =
  '/journeys/:journeyId/marketing-automations';
export const JOURNEY_MARKETING_AUTOMATION =
  '/journeys/:journeyId/marketing-automations/:automationId';
export const PARTNER_TRANSLATIONS = '/partner-translations';
export const PARTNER_TRANSLATION = '/partner-translations/:uuid';
export const MAILERS = '/mailers';
export const MAILER = '/mailers/:uuid';
// Sends a fixed message through this mailer's own transport, to prove it delivers.
export const MAILER_TEST = '/mailers/:uuid/test';
/**
 * Which mailer a journey's mail goes out through. Journey-scoped, not a
 * partner-wide default: the mailer is a property of the Mailcoach mailing list
 * the journey sends to, and that is what Mailcoach reads at send time.
 */
export const JOURNEY_MAILER = '/journeys/:journeyId/mailer';
// endregion

// region Notification Center (Laravel Partner API — see LARAVEL_ENDPOINTS above)
export const GET_NOTIFICATIONS = '/notifications';
export const GET_NOTIFICATIONS_UNREAD_COUNT = '/notifications/unread-count';
export const MARK_NOTIFICATION_READ = '/notifications/:notificationId/read';
export const MARK_ALL_NOTIFICATIONS_READ = '/notifications/read-all';
export const DELETE_NOTIFICATION = '/notifications/:notificationId';
// endregion

export const BILLING_DETAILS_API_PATH = '/billings/:billingId';
export const BILLING_OVERVIEW_API_PATH = '/billings';
export const FORGOT_PASSWORD_API_PATH = '/password/forgot';
export const GET_CUSTOMERS_API_PATH = '/journeys/:journeyId/customers';
export const GET_CUSTOMER_API_PATH = '/customers/:customerId';
export const GET_ALL_CUSTOMERS_API_PATH = '/customers';
export const CREATE_CUSTOMER_API_PATH = '/customers';
export const GET_RECENT_SERVICE_REQUEST_API_PATH =
  '/journeys/:journeyId/serviceRequests';
export const GET_RESULTS_STATS_API_PATH = '/journeys/:journeyId/results/stats';
export const GET_STYLES_STATS_API_PATH = '/journeys/:journeyId/styles/stats';
export const GET_ANALYTICS_FUNNEL_API_PATH = '/journeys/:journeyId/analytics';
export const GET_SUBSCRIBERS_STATS_API_PATH =
  '/journeys/:journeyId/subscribers/stats';
export const GET_SERVICE_REQUESTS_STATS_API_PATH =
  '/journeys/:journeyId/serviceRequests/stats';
export const GET_REGISTRATION_STATS_API_PATH =
  '/journeys/:journeyId/registrations/stats';
export const GET_CTA_CHART_API_PATH = '/journeys/:journeyId/conversions/chart';
export const GET_CTA_TABLE_API_PATH = '/journeys/:journeyId/conversions/table';
export const LOGIN_API_PATH = '/login';
export const LOGIN_ACCESS_TOKEN_API_PATH = '/auth/access-token';
export const CONFIRM_OTP_CODE = '/auth/confirm';
export const LOGIN_REFRESH_TOKEN_API_PATH = '/auth/refresh-token';
// Microsoft Entra SSO (Laravel-served, see LARAVEL_ENDPOINTS above)
export const SSO_MICROSOFT_LOGIN_API_PATH = '/auth/sso/microsoft';
export const SSO_EXCHANGE_API_PATH = '/auth/sso/exchange';
export const GET_ME_API_PATH = '/me';
export const EDIT_MY_PROFILE = '/me';
export const UPLOAD_AVATAR = '/avatar';
export const GET_PARTNERS = '/auth/partners';
export const GET_SETTINGS_API_PATH = '/settings';
export const UPDATE_USER_SETTINGS_API_PATH = '/settings';
export const GET_ALL_USER_API_PATH = '/users';
export const BULK_USERS_API_PATH = '/users/bulk';
export const CREATE_USER_API_PATH = '/users';
export const UPDATE_USER_API_PATH = '/users/:userId';

// Lead Sources management view (Laravel api/v4/partner). Distinct from the
// legacy CSV/webhook lead-source paths above which stay on the legacy app.
export const MANAGED_LEAD_SOURCES = '/lead-sources';
export const MANAGED_LEAD_SOURCE = '/lead-sources/:sourceId';
// Promote a lead source to the partner's default (writes partner.default_lead_source_id).
export const MANAGED_LEAD_SOURCE_DEFAULT = '/lead-sources/:sourceId/default';

export const DELETE_USER_API_PATH = '/users/:userId';
export const DISABLE_USER_API_PATH = '/users/:userId/disable';
export const ENABLE_USER_API_PATH = '/users/:userId/enable';
export const GET_ROLES_API_PATH = '/roles';
export const GET_USER_BRANCHES_API_PATH = '/branches';
export const GET_AVAILABLE_TIME_ZONES = '/timezones';
export const GET_USER_API_PATH = '/users/:userId';
export const UPDATE_CUSTOM_AVAILABILITY_API_PATH =
  '/users/:userId/custom-availabilities/:branchServiceId';
export const DELETE_CUSTOM_AVAILABILITY_API_PATH =
  '/users/:userId/custom-availabilities/:branchServiceId';
// DELETE — drops the user's users_integrations rows of that type (calendar
// types are disconnected first by the backend).
export const DELETE_USER_INTEGRATION_API_PATH =
  '/users/:userId/integrations/:integrationType';

export const GET_BOOKING_LINKS_API_PATH = '/users/:userId/bookingLinks';
export const CREATE_BOOKING_LINK_API_PATH = '/users/:userId/bookingLinks';
export const EDIT_BOOKING_LINK_API_PATH = '/bookingLinks/:bookingLinkId';
export const DELETE_BOOKING_LINK_API_PATH = '/bookingLinks/:bookingLinkId';

export const LOGOUT_API_PATH = '/logout';
export const RESET_PASSWORD_API_PATH = '/password/:token';
export const UNSUBSCRIBE_CUSTOMER_API_PATH = '/customers/:customerId';
export const GET_CUSTOMER_STATES_API_PATH = '/journeys/:journeyId/states';
export const CHANGE_CUSTOMER_STATE_API_PATH = '/customers/:customerId';
export const CHANGE_CUSTOMER_NOTES_API_PATH = '/customers/:customerId';

export const GET_GLANCE_DASHBOARD_API_PATH = '/journeys/:journeyId/stats';

export const CREATE_RESOURCE_FOR_PROJECT = '/projects/:projectId/resources';
export const DELETE_RESOURCE_FOR_PROJECT =
  '/projects/:projectId/resources/:resourceId';

export const GET_RESULT_CONFIG = '/config/result/:journeyId';
export const SAVE_RESULT_CONFIG = '/config/result/:journeyId';

export const GET_JOURNEY_CONFIG = '/journeys/:journeyId';
export const EDIT_JOURNEY_CONFIG =
  '/journeys/:journeyId/configurations/:configurationKey';
/**
 * Which lead sources own a configuration row and which follow the partner's
 * default. A literal path, so it is registered above `/journeys/:journeyId`
 * server-side — see `JourneyController::configurationScopes`.
 */
export const JOURNEY_CONFIGURATION_SCOPES = '/journeys/configuration-scopes';
/** Copy the default lead source's row onto a journey — see `deviateConfig`. */
export const DEVIATE_JOURNEY_CONFIG =
  '/journeys/:journeyId/configurations/:configurationKey/deviate';
/** A journey's own `tracking` row — see `JourneyTrackingConfigController`. */
export const JOURNEY_TRACKING_CONFIG = '/journeys/:journeyId/tracking';

export const GET_TAGS = '/journeys/:journeyId/tags';
export const CREATE_TAG = '/journeys/:journeyId/tags';
export const EDIT_TAG = '/journeys/:journeyId/tags/:tagId';
export const DELETE_TAG = '/journeys/:journeyId/tags/:tagId';

export const GET_TAG_GROUPS = '/journeys/:journeyId/taggroups';
export const CREATE_TAG_GROUP = '/journeys/:journeyId/taggroups';

export const GET_IMAGES = '/journeys/:journeyId/images';
export const UPLOAD_IMAGE = '/journeys/:journeyId/images/';
export const REMOVE_IMAGE = '/journeys/:journeyId/images';

export const DELETE_IMAGE_TAG =
  '/journeys/:journeyId/images/:imageId/tags/:tagId';
export const ADD_IMAGE_TAG = '/journeys/:journeyId/images/:imageId/tags/:tagId';

// region Email Promotions
export const FETCH_EMAIL_PROMOTION_LIST =
  '/journeys/:journeyId/emailPromotions';
export const FETCH_EMAIL_PROMOTION_TYPES =
  '/journeys/:journeyId/emailPromotionTypes';
export const ADD_EMAIL_PROMOTION = FETCH_EMAIL_PROMOTION_LIST;
export const EDIT_EMAIL_PROMOTION = '/emailPromotions/:emailPromotionId';
export const DELETE_EMAIL_PROMOTION = EDIT_EMAIL_PROMOTION;

export const FETCH_EMAIL_PROMOTION_CONDITIONS =
  '/journeys/:journeyId/promoConditions';
export const ADD_EMAIL_PROMOTION_CONDITION = FETCH_EMAIL_PROMOTION_CONDITIONS;
export const EDIT_EMAIL_PROMOTION_CONDITION =
  '/promoConditions/:emailPromotionConditionId';
export const DELETE_EMAIL_PROMOTION_CONDITION = EDIT_EMAIL_PROMOTION_CONDITION;

// endregion

export const FETCH_TAGS_STATS_2TAG_GROUPS =
  '/journeys/:journeyToken/tags/statistics/:tagGroup1Hashid/:tagGroup2Hashid';
export const FETCH_TAGS_STATS_3TAG_GROUPS =
  '/journeys/:journeyToken/tags/statistics/:tagGroup1Hashid/:tagGroup2Hashid/:tagGroup3Hashid';

export const LEAD_PROPERTIES = '/leads/properties';
export const GET_LEAD_MANAGEMENT_PERFORMANCE_CHART_DATA = '/leads/metrics';
export const GET_LEAD_MANAGEMENT_FUNNEL_CHART_DATA = '/leads/funnel-chart/';
export const GET_LEAD_MANAGEMENT_ROI_FUNNEL_CHART_DATA =
  '/leads/roi-funnel-report/';
export const GET_LEAD_MANAGEMENT_TEAM_REPORT = '/leads/performance-chart/';
export const GET_LEAD_MANAGEMENT_CALL_BACK_CHART_DATA = '/leads/calls-chart';

export const GET_LEAD_FIELDS = '/leads/fields';
export const CREATE_LEAD_MANUAL_SOURCE = '/leadSources/:sourceId/leads';
export const CREATE_LEAD = '/leads';
export const EDIT_LEAD = '/leads/:leadId';

// region Contacts (B2B customers, Laravel)
export const CUSTOMER_CONTACTS = '/customers/:customerId/contacts';
export const CUSTOMER_CONTACT = '/customers/:customerId/contacts/:contactId';
export const CUSTOMER_PRIMARY_CONTACT = '/customers/:customerId/primary-contact';
export const LEAD_CONTACTS = '/leads/:leadId/contacts';
export const LEAD_PRIMARY_CONTACT = '/leads/:leadId/primary-contact';
// Paginated contacts of every customer the user may see.
export const CONTACTS = '/contacts';
// endregion
export const GET_LEAD = '/leads/:leadId';
export const GET_LEAD_SENT_MAILS = '/leads/:leadId/sent-mails';
export const GET_LEAD_LINKED_LEADS = '/leads/:leadId/linked-leads';
export const GET_LEADS = '/leads';
export const GET_COLD_PROSPECTS_OVERVIEW = '/leads/cold-prospects';

// region Lead states
export const LEAD_STATES = '/leadStates';
export const LEAD_STATE_APPOINTMENT_EVENTS = '/leads/settings';
export const CHANGE_LEAD_STATE = '/leadStates/:stateId';
export const REORDER_LEAD_STATE = '/leadStates/reorder';
// endregion

export const CREATE_LEAD_SOURCE_CSV = '/leadSources/';
export const CREATE_LEADS_CSV = '/leadSources/:sourceId/leads';
export const DELETE_LEAD_SOURCE_CSV = '/leadSources/:sourceId';
export const GET_LEAD_SOURCE = '/leadSources/:sourceId';
export const UPDATE_LEAD_SOURCE_CSV = '/leadSources/:sourceId';

export const GET_SERVICE_REQUESTS = '/serviceRequests'
export const BULK_SERVICE_REQUESTS = '/serviceRequests/bulk'
export const BULK_LEADS = '/leads/bulk'
export const GET_MULTI_BOOKING_LIST = '/serviceRequests/multiBookingsList'
export const RESCHEDULE_SERVICE_REQUEST = '/serviceRequests/:serviceRequestId/reschedule'
export const GET_SINGLE_SERVICE_REQUEST = '/serviceRequests/:serviceRequestId'
export const EDIT_SERVICE_REQUEST = '/serviceRequests/:serviceRequestId'
export const CANCEL_SERVICE_REQUEST = '/serviceRequests/:serviceRequestId/cancel'
export const CONFIRM_SERVICE_REQUEST = '/serviceRequests/:serviceRequestId/confirm'
export const DECLINE_SERVICE_REQUEST = '/serviceRequests/:serviceRequestId/decline'
// Laravel-only (kebab-case): bookings of the filtered calendar's agents that
// sit on branch services OUTSIDE the filter — used for busy-slot shading.
export const GET_CALENDAR_UNAVAILABILITY = '/service-requests/calendar-unavailability'
export const BOOK_APPOINTMENT = '/leads/:leadId/appointments'

export const ADD_CALENDAR_EVENT = '/calendar-events';
export const GET_CALENDAR_EVENTS = '/calendar-events';
export const EDIT_CALENDAR_EVENT = '/calendar-events/:calendarEventId';
export const DELETE_CALENDAR_EVENT = '/calendar-events/:calendarEventId';
export const ADD_RECURRING_CALENDAR_EVENT = '/calendar-events/recurring';

export const GET_LEAD_TASKS = '/tasks'
export const GET_LEAD_TASK_DETAILS = '/tasks/:taskId'
export const CREATE_LEAD_TASK = '/tasks'
export const CREATE_TASK_FOR_LEAD = '/leads/:leadId/tasks'
export const EDIT_LEAD_TASK = '/tasks/:taskId'
export const DELETE_LEAD_TASK = '/tasks/:taskId'

export const FETCH_UI_PREFERENCES = '/settings';
export const UI_PREFERENCES = '/settings/:key';
export const SET_PARTNER_PREFERENCES = '/partners/config/:key';

export const FETCH_PARTNER_CONFIG = '/partners/config';
export const SET_PARTNER_CONFIG = '/partners/config';
export const UPDATE_CUSTOMER_REQUIRED_FIELDS =
  '/partners/config/customer-required-fields';
export const PARTNER_LOGO = '/partners/logo';

export const FETCH_LEAD_ASSIGNEES = '/leads/assignees'
export const FETCH_LEAD_SOURCES = '/leads/leadSources'
export const FETCH_LEAD_SERVICES = '/leads/services'
export const FETCH_LEAD_STATUSES = '/leads/statuses'
export const ASSIGN_LEAD = '/leads/assign'
export const ARCHIVE_LEAD = '/leads/archive'
export const COMMENT_ON_LEAD = '/leads/:leadId/comments'
export const BATCH_ASSIGN_LEADS = '/agents/:assigneeId/leads';


export const FETCH_EMAIL_AUTOMATION_VARIABLES = '/emailAutomations/fields';
export const FETCH_EMAIL_AUTOMATION = '/emailAutomations';
export const CREATE_EMAIL_AUTOMATION = '/emailAutomations';
export const UPDATE_EMAIL_AUTOMATION = '/emailAutomations/:id';
export const DELETE_EMAIL_AUTOMATION = '/emailAutomations/:id';

export const GET_BRANCHES = '/branches';
export const SEARCH_BRANCHES = '/branches/search';
export const GET_MANAGED_BRANCHES = '/managedBranches';
// Migrated to Laravel API - using kebab-case naming convention
export const GET_BRANCH_SERVICES = '/branch-services';
export const CREATE_SERVICE_API_PATH = '/branch-services';
export const DELETE_BRANCH_API_PATH = '/branches/:branchId';
export const DELETE_SERVICE_API_PATH = '/branch-services/:serviceId';
export const UPDATE_BRANCH_API_PATH = '/branches/:branchId';
export const UPDATE_SERVICE_API_PATH = '/branch-services/:serviceId';
export const BRANCH_THUMBNAIL = '/branches/:branchId/thumbnail';
export const BRANCH_SERVICE_THUMBNAIL = '/branch-services/:serviceId/thumbnail';
export const FETCH_AGENT_AVAILABILITY =
  '/branch-services/:branchServiceId/availability/:agentId';
export const FETCH_SERVICE_AVAILABILITY =
  '/branch-services/:branchServiceId/availability';
export const GET_SERVICE = '/branch-services/:serviceId';

export const CREATE_BRANCH_API_PATH = '/branches';
export const GET_BRANCH = 'branches/:branchId';
export const FETCH_AGENTS = '/staff';

export const FETCH_SERVICE_TYPES = '/serviceTypes';
export const CREATE_SERVICE_TYPE = '/serviceTypes';
export const EDIT_SERVICE_TYPE = '/serviceTypes/:serviceTypeId';
export const DELETE_SERVICE_TYPE = '/serviceTypes/:serviceTypeId';
export const UPDATE_ALL_SERVICES_FOR_SERVICE_TYPE =
  '/serviceTypes/:serviceTypeId/update-all-services';
export const GUESS_SERVICE_TYPE_ICON = '/serviceTypes/guess-icon';
export const UPLOAD_THUMBNAIL_FOR_SERVICE_TYPE =
  '/serviceTypes/:serviceTypeId/thumbnail';
export const REORDER_SERVICE_TYPES = '/serviceTypes/reorder';

export const FETCH_OFFERINGS = '/offerings';
export const CREATE_OFFERING = '/offerings';
export const EDIT_OFFERING = '/offerings/:offeringId';
export const DELETE_OFFERING = '/offerings/:offeringId';

export const FETCH_PRODUCT_GROUPS = '/product-groups';
export const CREATE_PRODUCT_GROUP = '/product-groups';
export const EDIT_PRODUCT_GROUP = '/product-groups/:productGroupId';
export const DELETE_PRODUCT_GROUP = '/product-groups/:productGroupId';
export const UPLOAD_THUMBNAIL_FOR_PRODUCT_GROUP =
  '/productGroups/:productGroupId/image';

export const FETCH_PERMISSIONS_BY_CATEGORY = '/permissions';
export const FETCH_PERMISSIONS_BY_ROLE = '/roles?all=1';
export const CREATE_ROLE = '/roles';
export const UPDATE_ROLE = '/roles/:roleId';
export const DELETE_ROLE = '/roles/:roleId';
export const REORDER_ROLE = '/roles/reorder';

// Not used now. Kept for compatibility
export const UPDATE_ROLE_PERMISSIONS = '/roles/:roleId/permissions';

export const FETCH_CALLS = '/calls';

// Sales Influencers (Laravel Partner API — see LARAVEL_ENDPOINTS above).
// Read-only in the back office: creation/assignment is programmatic (API only).
export const FETCH_SALES_INFLUENCERS = '/sales-influencers';

// Agent skills (Laravel Partner API — see LARAVEL_ENDPOINTS above).
export const FETCH_SKILLS = '/skills';
export const CREATE_SKILL = '/skills';
export const UPDATE_SKILL = '/skills/:skillId';
export const DELETE_SKILL = '/skills/:skillId';

// Extra Field Types - migrated to Laravel
export const FETCH_EXTRA_FIELD_TYPES = '/extra-field-types';
export const CREATE_EXTRA_FIELD_TYPE = '/extra-field-types';
export const EDIT_EXTRA_FIELD_TYPE = '/extra-field-types/:extraFieldTypeId';
export const DELETE_EXTRA_FIELD_TYPE = '/extra-field-types/:extraFieldTypeId';
export const REORDER_EXTRA_FIELD_TYPE = '/extra-field-types/:relatedTo/reorder';
export const GET_EXTRA_FIELD_TYPE_VALUE_COUNT =
  '/extra-field-types/:extraFieldTypeId/values/count';

export const CREATE_EXTRA_FIELD_TYPE_OPTION =
  '/extraFieldTypes/:extraFieldTypeId/options';
export const EDIT_EXTRA_FIELD_TYPE_OPTION =
  '/extraFieldOptions/:extraFieldTypeOptionId';
export const DELETE_EXTRA_FIELD_TYPE_OPTION =
  '/extraFieldOptions/:extraFieldTypeOptionId';

export const SET_EXTRA_FIELD_VALUE =
  '/extraFieldTypes/:extraFieldTypeId/set/:leadId';
export const CLEAR_EXTRA_FIELD_VALUE =
  '/extraFieldTypes/:extraFieldTypeId/clear/:leadId';

export const FETCH_LEAD_ACTIVITIES = '/leads/:leadId/activities';

export const UPLOAD_FILE_FOR_LEAD = '/leads/:leadId/files';
export const REMOVE_LEAD_FILE = '/lead-files/:leadFileId';
export const REMOVE_CUSTOMER_FILE = '/customer-files/:customerFileId';

export const FETCH_PARTNER_INTEGRATIONS = '/integrations';
export const FETCH_INTEGRATION_TYPES = '/integration-types';

// region Trickpark Integration (Laravel Partner API — see LARAVEL_ENDPOINTS above).
// Lifecycle (enable/disable) uses the generic INTEGRATION_GENERIC_* paths.
export const TRICKPARK_INTEGRATION_STATUS = '/integrations/trickpark'
export const TRICKPARK_USER_MAPPINGS = '/integrations/trickpark/user-mappings'
export const TRICKPARK_USER_MAPPING = '/integrations/trickpark/user-mappings/:mappingId'
export const TRICKPARK_SESSIONS = '/integrations/trickpark/sessions'
// endregion

// Task Automation API paths
export const FETCH_TASK_AUTOMATIONS = '/taskAutomations'
export const CREATE_TASK_AUTOMATION = '/taskAutomations'
export const GET_TASK_AUTOMATION = '/taskAutomations/:taskAutomationId'
// Form-builder metadata (trigger/consequence/retire types + lead_conditions schema) — CakePHP
export const FETCH_TASK_AUTOMATION_META = '/taskAutomations/details'
export const EDIT_TASK_AUTOMATION = '/taskAutomations/:taskAutomationId'
export const DELETE_TASK_AUTOMATION = '/taskAutomations/:taskAutomationId'

export const DO_TASK_ACTION = '/tasks/:taskId/action/:actionId';

// Support API paths
export const CREATE_SUPPORT_TICKET = '/support';
export const GET_SUPPORT_TICKETS = '/support';
export const GET_SUPPORT_TICKET = '/support/:ticketId';
export const GET_SUPPORT_TICKET_COMMENTS = '/support/:ticketId/comments';
export const CREATE_SUPPORT_TICKET_COMMENT = '/support/:ticketId/comments';
export const ATTACH_FILE_TO_SUPPORT_TICKET = '/support/:ticketId/attachment';

export const INTEGRATION_OUTLOOK_PARTNER_ENABLE = '/outlook/integration/enable';
export const INTEGRATION_OUTLOOK_PARTNER_DISABLE =
  '/outlook/integration/disable';
export const INTEGRATION_OUTLOOK_USER_CONNECT = '/outlook/users/connection';
export const INTEGRATION_OUTLOOK_USER_DISCONNECT = '/outlook/users/connection';

export const INTEGRATION_GOOGLE_PARTNER_ENABLE = '/google/integration/enable';
export const INTEGRATION_GOOGLE_PARTNER_DISABLE = '/google/integration/disable';
export const INTEGRATION_GOOGLE_USER_CONNECT = '/google/users/connection';
export const INTEGRATION_GOOGLE_USER_DISCONNECT = '/google/users/connection';

export const INTEGRATION_CONFIGURATION_EDIT = '/integrations/:integrationType';

// region Carat integration (Laravel Partner API — see LARAVEL_ENDPOINTS above).
// Installing provisions the Carat lead source, its scoped Public API key and the
// ingest id the partner pastes into Carat.
export const CARAT_INTEGRATION = '/integrations/carat';
export const CARAT_PLANNINGS = '/integrations/carat/plannings';
export const CARAT_PLANNING = '/integrations/carat/plannings/:planningId';
// endregion

// Generic integration routes (used by credential-based integrations e.g. Exchange EWS)
export const INTEGRATION_GENERIC_ENABLE =
  '/integrations/:integrationType/enable';
export const INTEGRATION_GENERIC_DISABLE =
  '/integrations/:integrationType/disable';
export const INTEGRATION_GENERIC_TEST_CONNECTION_PUBLIC =
  '/integrations/:integrationType/test-connection/public';

// office365_mail — lead mail through Microsoft Graph (Laravel-served)
export const LEAD_MAIL_THREADS = '/leads/:leadId/mail-threads';
export const LEAD_MAIL_THREAD_MESSAGES = '/leads/:leadId/mail-threads/messages';
export const LEAD_MAIL_MESSAGE = '/leads/:leadId/mail-messages/:messageId';
export const LEAD_MAIL_ATTACHMENT =
  '/leads/:leadId/mail-messages/:messageId/attachments/:attachmentId';
export const LEAD_MAIL_SEND = '/leads/:leadId/mail/send';
export const LEAD_MAIL_REPLY = '/leads/:leadId/mail-messages/:messageId/reply';
export const MAIL_MAILBOXES = '/mail/mailboxes';
export const MAIL_MAILBOXES_SYNC = '/mail/mailboxes/sync';
export const MAIL_MAILBOX = '/mail/mailboxes/:address';
export const MAIL_MAILBOX_USERS = '/mail/mailboxes/users';
export const MAIL_MAILBOX_USER_OVERRIDE = '/mail/mailboxes/users/:userUuid';
export const MAIL_MY_MAILBOX = '/mail/me/mailbox';
export const MAIL_CAPABILITIES = '/mail/capabilities';
export const EMAIL_TEMPLATE_RENDER_PREVIEW = '/email-templates/:uuid/render-preview';

// region Sales Goal Plans (Laravel Partner API — see LARAVEL_ENDPOINTS above).
// One plan per period; its tree partner → offering → branch → branch service
// → agent carries revenue / deals / leads targets plus a backend-calculated
// progress snapshot (conversional-laravel docs/goals.md).
export const FETCH_GOAL_PLANS = '/goal-plans'
export const LOOKUP_GOAL_PLAN = '/goal-plans/lookup'
export const FETCH_MY_GOAL_PLANS = '/goal-plans/mine'
export const CREATE_GOAL_PLAN = '/goal-plans'
export const GET_GOAL_PLAN = '/goal-plans/:planId'
export const DELETE_GOAL_PLAN = '/goal-plans/:planId'
export const UPDATE_GOAL_PLAN_TARGETS = '/goal-plans/:planId/targets'
export const RECALCULATE_GOAL_PLAN = '/goal-plans/:planId/recalculate'
// endregion

// API Keys
export const FETCH_API_KEYS = '/api-keys';
export const CREATE_API_KEY = '/api-keys';
export const GET_API_KEY = '/api-keys/:apiKeyId';
export const UPDATE_API_KEY = '/api-keys/:apiKeyId';
export const PATCH_API_KEY = '/api-keys/:apiKeyId';
export const DELETE_API_KEY = '/api-keys/:apiKeyId';
export const REGENERATE_API_KEY = '/api-keys/:apiKeyId/regenerate';
export const FETCH_API_KEY_SCOPES = '/api-keys/scopes';
